import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Building2,
  Camera,
  CheckCircle2,
  FileSpreadsheet,
  Percent,
  ScanLine,
  ShieldCheck,
  Truck,
  Wallet,
  Wrench,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { COMPANY } from "../content/legal";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n";
import { planName } from "../lib/plans";

const STEPS = [
  { icon: Camera, key: "step1" },
  { icon: ScanLine, key: "step2" },
  { icon: Percent, key: "step3" },
];

const FEATURES = [
  { icon: ShieldCheck, key: "f1" },
  { icon: BarChart3, key: "f2" },
  { icon: Wallet, key: "f3" },
  { icon: FileSpreadsheet, key: "f4" },
];

const AUDIENCE = [
  { icon: Truck, key: "audience1" },
  { icon: Wrench, key: "audience2" },
  { icon: Building2, key: "audience3" },
];

// keep in sync with backend/billing.py PLANS
const PLANS = [
  { key: "starter", price: "€19", seat: "€9", scans: 150, seats: 10, extra: "€0.13" },
  { key: "growth", price: "€49", seat: "€7", scans: 750, seats: 50, popular: true, extra: "€0.11" },
  { key: "scale", price: "€149", seat: "€5", scans: 5000, unlimitedSeats: true, extra: "€0.09" },
  { key: "enterprise", price: "€299", seat: "€5", scans: 15000, unlimitedSeats: true, extra: "€0.09" },
];

const FAQ = ["faq1", "faq2", "faq3", "faq4"];

const Landing = () => {
  const { t } = useI18n();
  return (
  <div className="min-h-screen bg-background">
    <header className="sticky top-0 z-30 border-b bg-card/90 backdrop-blur">
      <div className="mx-auto flex max-w-[1200px] items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <BarChart3 className="h-4 w-4" />
          </span>
          <span className="font-display text-base font-semibold tracking-tight">LedgerLens</span>
        </div>
        <div className="flex min-w-0 shrink items-center gap-1.5 sm:gap-2">
          <LanguageSwitcher showIcon={false} />
          <Link to="/login" data-testid="landing-login-link" className="shrink-0">
            <Button variant="ghost" size="sm">
              {t("common.signIn")}
            </Button>
          </Link>
          <Link to="/signup" data-testid="landing-start-free-button" className="hidden shrink-0 sm:block">
            <Button size="sm">{t("landing.pricingCta")}</Button>
          </Link>
        </div>
      </div>
    </header>

    <section className="ll-hero-glow relative border-b">
      <div className="ll-noise absolute inset-0 opacity-70" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-[1200px] gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:py-20">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <ScanLine className="h-3.5 w-3.5 text-primary" /> {t("landing.badge")}
          </span>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
            {t("landing.h1")}
          </h1>
          <p className="mt-4 max-w-xl text-base text-muted-foreground md:text-lg">
            {t("landing.sub")}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/signup">
              <Button size="lg" data-testid="hero-signup-button">
                {t("landing.ctaPrimary")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" data-testid="hero-login-button">
                {t("landing.ctaSecondary")}
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("landing.trialNote")}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
            {AUDIENCE.map((a) => (
              <span key={a.key} className="flex items-center gap-2 text-sm text-muted-foreground">
                <a.icon className="h-4 w-4 text-primary" /> {t(`landing.${a.key}`)}
              </span>
            ))}
          </div>
        </div>

        <div className="ll-card overflow-hidden">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("landing.liveExtraction")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs text-teal-800">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t("status.parsed")}
            </span>
          </div>
          <dl className="divide-y text-sm">
            {[
              [t("common.vendor"), "INA d.d. — Zagreb Zapad"],
              [t("common.receiptNo"), "1187-2-1"],
              [t("common.date"), "14.03.2026."],
              [t("common.category"), t("landing.demoCategory")],
              [t("dash.netBase"), "€56.02"],
              [`${t("common.vat")} 25%`, "€14.00"],
              [t("common.total"), "€70.02"],
              [`${t("common.refund")} (100% ${t("common.vat")})`, "€14.00"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-4 px-4 py-2.5">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="ll-num font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-[1200px] px-4 py-14 sm:px-6">
      <h2 className="font-display text-2xl font-semibold">{t("landing.howTitle")}</h2>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <div key={s.key} className="ll-card p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <s.icon className="h-4 w-4" />
              </span>
              <span className="ll-num text-xs font-medium text-muted-foreground">
                {t("landing.step")} {i + 1}
              </span>
            </div>
            <h3 className="mt-3 font-display text-lg font-semibold">{t(`landing.${s.key}Title`)}</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">{t(`landing.${s.key}Body`)}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="border-y bg-card">
      <div className="mx-auto max-w-[1200px] px-4 py-14 sm:px-6">
        <h2 className="font-display text-2xl font-semibold">{t("landing.featuresTitle")}</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.key} className="rounded-xl border p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <f.icon className="h-4 w-4" />
              </span>
              <h3 className="mt-3 font-display text-base font-semibold">{t(`landing.${f.key}Title`)}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{t(`landing.${f.key}Body`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-[1200px] px-4 py-14 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">{t("landing.pricingTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("landing.pricingSub")}
          </p>
        </div>
        <Link to="/signup">
          <Button variant="outline">{t("landing.pricingCta")}</Button>
        </Link>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PLANS.map((p) => (
          <div
            key={p.key}
            className={`ll-card p-5 ${p.popular ? "border-primary ring-1 ring-primary/30" : ""}`}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">{planName(t, p.key, p.key)}</h3>
              {p.popular ? (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {t("billing.popular")}
                </span>
              ) : null}
            </div>
            <div className="mt-3 flex items-end gap-1">
              <span className="ll-num font-display text-3xl font-semibold">{p.price}</span>
              <span className="text-sm text-muted-foreground">{t("common.perMonth")}</span>
            </div>
            <p className="ll-num mt-1 text-sm text-muted-foreground">
              {t("landing.seatPrice", { price: p.seat })}
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="ll-num">
                  {p.scans.toLocaleString("hr-HR")} {t("landing.monthlyIncluded")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                {p.unlimitedSeats
                  ? t("landing.unlimitedUsers")
                  : t("landing.upToSeats", { n: p.seats })}
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="ll-num">{t("landing.extraScan", { price: p.extra })}</span>
              </li>
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section className="border-t bg-card">
      <div className="mx-auto max-w-[900px] px-4 py-14 sm:px-6">
        <h2 className="font-display text-2xl font-semibold">{t("landing.faqTitle")}</h2>
        <div className="mt-6 divide-y">
          {FAQ.map((key) => (
            <div key={key} className="py-4">
              <h3 className="font-medium">{t(`landing.${key}Q`)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t(`landing.${key}A`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <footer className="border-t">
      <div className="mx-auto flex max-w-[1200px] flex-col gap-3 px-4 py-8 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <span>
          © {new Date().getFullYear()} {COMPANY.legalName} — {t("landing.footerRights")}
        </span>
        <div className="flex flex-wrap items-center gap-4">
          <Link to="/legal/terms" className="hover:text-foreground" data-testid="footer-terms-link">
            {t("landing.legalTerms")}
          </Link>
          <Link to="/legal/privacy" className="hover:text-foreground" data-testid="footer-privacy-link">
            {t("landing.legalPrivacy")}
          </Link>
          <Link to="/legal/refund" className="hover:text-foreground" data-testid="footer-refund-link">
            {t("landing.legalRefund")}
          </Link>
          <a href={`mailto:${COMPANY.support}`} className="hover:text-foreground">
            {COMPANY.support}
          </a>
        </div>
      </div>
    </footer>
    </div>
  );
};

export default Landing;
